import React, { useEffect, useRef, useState, Suspense } from 'react';
import { MeetingContext, isClientContext } from '../../types/booking';
import { FaisOverlay } from './FaisOverlay';
import { bookingService } from '../../services/bookingService';
import { GlossaryTooltip } from '../knowledge/GlossaryTooltip';
import { useTTS } from '../../hooks/useTTS';
import { useAvatarState } from '../../hooks/useAvatarState';
import BankerAvatar from '../avatar/BankerAvatar';
import { PermissionGate } from '../PermissionGate';

const BankerAvatar3D = React.lazy(() => import('../avatar/BankerAvatar3D'));

// FAIS disclaimer text (mirrors backend FAIS_DISCLAIMER constant in booking.py).
// IMPORTANT: Keep in sync with backend/src/api/routers/booking.py:FAIS_DISCLAIMER.
const FAIS_DISCLAIMER = `IMPORTANT REGULATORY NOTICE — FINANCIAL ADVISORY SERVICES ACT (FAIS)

Marome Investments (Pty) Ltd is an authorised Financial Services Provider (FSP) licensed
under the Financial Advisory and Intermediary Services Act, 37 of 2002.

1. NATURE OF ADVICE: The information and recommendations provided during this meeting
   constitute financial advice as defined under FAIS. This advice is based on the personal
   financial information you have provided and your stated investment objectives.

2. SUITABILITY: Advice will be tailored to your personal financial situation, needs, and
   risk tolerance. You are encouraged to disclose all relevant financial information.

3. CONFLICTS OF INTEREST: Marome Investments maintains a Conflict of Interest Management
   Policy. Details are available on request.

4. COMPLAINTS: Should you have a complaint regarding the advice received, you may contact
   the FAIS Ombud at 0860 FAISOM (324766) or www.faisombud.co.za.

5. RECORDING: This session may be recorded for quality assurance and regulatory compliance
   purposes in accordance with POPIA.

By acknowledging this notice, you confirm that you have read and understood the above
disclosures and consent to receiving financial advice during this session.`;

interface Message {
  id?: string;
  role: 'client' | 'banker';
  content: string;
}

interface MeetingSessionProps {
  context: MeetingContext;
  onEnded?: () => void;
}

const API_URL = (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL ?? 'http://localhost:8001';

const MeetingSession: React.FC<MeetingSessionProps> = ({ context, onEnded }) => {
  const isClient = isClientContext(context);
  const [faisRequired, setFaisRequired] = useState(!context.fais_acknowledged);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [input, setInput] = useState('');
  const [ended, setEnded] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // TTS + avatar
  const tts = useTTS();
  const { avatarState } = useAvatarState({
    isQueuePlaying: tts.isQueuePlaying,
    isPlaying: tts.isPlaying,
    isStreaming: isTyping,
  });

  const bookingId = isClient ? context.booking_id : null;

  // AC:8 — guest-appropriate salutation prepended to the disclaimer
  const faisContent =
    !isClient && context.guest_name
      ? `Dear ${context.guest_name},\n\n${FAIS_DISCLAIMER}`
      : FAIS_DISCLAIMER;

  // AC:9 — fetch message history (used on reconnect)
  const fetchHistory = async (id: string) => {
    try {
      const msgs = await bookingService.getMeetingMessages(id);
      setMessages(
        msgs.map(m => ({ id: m.id, role: m.role as 'client' | 'banker', content: m.content })),
      );
    } catch {
      // non-critical — fresh state is fine
    }
  };

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Load message history on initial mount so prior conversation is visible immediately.
  // Speaks the last banker message so the voice resumes on re-entry.
  useEffect(() => {
    if (faisRequired || !isClient || !bookingId) return;
    (async () => {
      try {
        const msgs = await bookingService.getMeetingMessages(bookingId);
        const mapped = msgs.map(m => ({ id: m.id, role: m.role as 'client' | 'banker', content: m.content }));
        setMessages(mapped);
        const lastBanker = [...mapped].reverse().find(m => m.role === 'banker');
        if (lastBanker) tts.speak(lastBanker.content, Date.now());
      } catch {
        // non-critical — fresh session is fine
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faisRequired, bookingId, isClient]);

  // AC:9 — SSE connection with exponential backoff (max 3 retries)
  useEffect(() => {
    if (faisRequired || !isClient || !bookingId) return;

    const jwt = localStorage.getItem('token') ?? '';

    const connect = (attempt = 0) => {
      const url = `${API_URL}/api/v1/bookings/${bookingId}/meeting/stream?token=${encodeURIComponent(jwt)}`;
      const es = new EventSource(url);

      es.onmessage = (e: MessageEvent) => {
        try {
          const event = JSON.parse(e.data as string) as {
            type: string;
            role?: string;
            content?: string;
          };
          if (event.type === 'message' && event.role && event.content) {
            setIsTyping(false);
            const msg = { role: event.role as 'client' | 'banker', content: event.content! };
            setMessages(prev => [...prev, msg]);
            if (event.role === 'banker') {
              tts.speak(event.content!, Date.now());
            }
          } else if (event.type === 'typing') {
            setIsTyping(true);
          } else if (event.type === 'end') {
            setEnded(true);
            es.close();
            onEnded?.();
          }
          // 'ping' events are keepalives — ignore
        } catch {
          // malformed event — ignore
        }
      };

      es.onerror = () => {
        es.close();
        if (attempt < 3) {
          // AC:9 — restore history on reconnect before retrying
          fetchHistory(bookingId);
          setTimeout(() => connect(attempt + 1), Math.pow(2, attempt) * 1000);
        }
      };

      eventSourceRef.current = es;
    };

    connect();
    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faisRequired, bookingId, isClient]);

  const sendMessage = async () => {
    if (!input.trim() || !bookingId) return;
    const content = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'client', content }]);
    try {
      await bookingService.sendMeetingMessage(bookingId, content);
    } catch {
      // non-critical — message is shown optimistically
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') sendMessage();
  };

  // Show FAIS overlay until acknowledged
  if (faisRequired) {
    return (
      <FaisOverlay
        content={faisContent}
        bookingId={bookingId}
        onAcknowledged={() => setFaisRequired(false)}
      />
    );
  }

  // Guest view: simple in-session screen (no chat)
  if (!isClient) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-3 h-3 rounded-full bg-teal-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-300 text-sm">Meeting in session</p>
          <p className="text-slate-500 text-xs mt-1">{context.room_name}</p>
        </div>
      </div>
    );
  }

  // Session ended
  if (ended) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-teal-900/40 flex items-center justify-center mb-4 mx-auto">
            <svg
              className="w-6 h-6 text-teal-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-slate-200 font-medium">Meeting Complete</p>
          <p className="text-slate-400 text-sm mt-1">
            Thank you for banking with my investments.
          </p>
        </div>
      </div>
    );
  }

  // Client chat UI — immersive meeting room with avatar
  const roomName = isClientContext(context) ? context.room_name : 'Meeting Room';

  return (
    <div className="fixed inset-0 bg-gray-950 flex flex-col" data-testid="meeting-chat">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3 shrink-0">
        <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
        <span className="text-gray-300 text-sm font-medium">{roomName}</span>
      </div>

      {/* Banker avatar — fixed height so input is always accessible */}
      <div className="shrink-0 flex flex-col items-center justify-center bg-slate-900 border-b border-gray-800" style={{ height: 360 }}>
        <PermissionGate
          permission="feature:banker-avatar-3d"
          fallback={
            <BankerAvatar state={avatarState} persona="banker" size="xl" />
          }
        >
          <Suspense fallback={
            <BankerAvatar state={avatarState} persona="banker" size="xl" />
          }>
            <BankerAvatar3D
              state={avatarState}
              persona="banker"
              size="xl"
              modelOffset={[0, -1.4, 0]}
              audioRef={tts.audioRef}
            />
          </Suspense>
        </PermissionGate>
        <p className="text-amber-400 text-xs font-medium mt-2 tracking-wide uppercase">Investment Banker</p>
        {isTyping && (
          <p className="text-gray-500 text-xs mt-1 animate-pulse" data-testid="typing-indicator">
            typing…
          </p>
        )}
      </div>

      {/* Message thread */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-6 space-y-3" data-testid="message-list">
        {messages.length === 0 && (
          <p className="text-gray-600 text-sm text-center mt-4">Your banker is ready. Start the conversation below.</p>
        )}
        {messages.map((m, i) => (
          <div
            key={m.id ?? i}
            className={`flex ${m.role === 'client' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === 'client'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-gray-800 text-gray-100 rounded-bl-sm'
              }`}
            >
              {m.role === 'banker' ? <GlossaryTooltip>{m.content}</GlossaryTooltip> : m.content}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="p-4 border-t border-gray-800 flex gap-2 shrink-0">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-gray-800 text-white rounded-full px-4 py-2 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          placeholder="Speak to your banker…"
          data-testid="message-input"
          disabled={ended}
        />
        <button
          onClick={sendMessage}
          disabled={ended || !input.trim()}
          className="px-5 py-2 bg-amber-500 text-black rounded-full text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="send-btn"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default MeetingSession;
